import { types, getEnv, getRoot, flow, getSnapshot } from 'mobx-state-tree';
import { v4 as uuidv4 } from 'uuid';
import { cloneDeep } from 'lodash-es';
import { MobxEnvironment } from '../mobx-environment';
import { getOidcUser } from '../auth';
import { RequestStatusModel, RequestStatus } from '../../libs/helpers';
import {
  DigitalOrdersBase,
  OrderStatus,
  Organization,
  DigitalOrdersSaveResponse,
  DigitalOrdersSendResponse,
  GetAllOrganizationsResult,
  GetAllDigitalOrdersResult,
  NewOrExisting,
  DigitalOrderOrganization,
  ExistingDigitalOrderOrganization,
  GetSomeFacilitiesResult,
  GetAllCountriesResult,
  GetAllStatesResult,
  RemoveDigitalOrderByIdResult,
  FullFacility,
} from '../../models';
import {
  DigitalOrdersBaseModel,
  // The following types describe server response.
  OrganizationModel,
  CountryModel,
  StateModel,
  FullFacilityModel,
} from './DigitalOrdersStore.models';
import { ProductPricingMap } from '../../components/Product/pricingUtils';

export const DigitalOrdersStoreModel = types
  .model({
    digitalOrders: types.map(DigitalOrdersBaseModel),
  })
  .props({
    currentOrderId: types.maybeNull(types.string),
    organizationMap: types.map(OrganizationModel),
    facilityMap: types.map(FullFacilityModel),
    countries: types.array(CountryModel),
    states: types.array(StateModel),
    organizationsRequestStatus: RequestStatusModel,
    facilityRequestStatus: RequestStatusModel,
    countryRequestStatus: RequestStatusModel,
    stateRequestStatus: RequestStatusModel,
    productRequestStatus: RequestStatusModel,
    submitDigitalOrderRequestStatus: RequestStatusModel,
  })
  .views(self => ({
    get environment() {
      return getEnv(self) as MobxEnvironment;
    },
    get rootStore() {
      return getRoot(self) as any;
    },
    order(id: string) {
      return (id && self.digitalOrders.get(id)) || null;
    },
  }))
  .views(self => ({
    get organizations() {
      return Object.keys(self.organizationMap.toJSON())
        .map(id => self.organizationMap.get(id) as Organization)
        .sort((a, b) => (a.name > b.name ? 1 : -1));
    },
    facilitiesForSelectedOrganization(orderId: string) {
      let org = self.order(orderId)?.organization as DigitalOrderOrganization;
      if (!org) {
        return [];
      }
      if (org.newOrExistingOrganization === NewOrExisting.NEW) {
        return [];
      }
      org = org as ExistingDigitalOrderOrganization;
      return self.organizationMap
        .get(org.organizationId.toString())
        .facilityIds.map(id => {
          return self.facilityMap.get(id.toString());
        })
        .filter(f => !!f) // filter undefined.
        .sort((a, b) => (a.name > b.name ? 1 : -1));
    },
    getFacilityById(id: string) {
      return self.facilityMap.get(id);
    },
    getStatesForSelectedCountry(countryId) {
      if (!countryId) {
        return [];
      } else {
        return self.states.filter(s => s.countryId === countryId);
      }
    },
    get existingOrganizationNames() {
      return Array.from(self.organizationMap.values()).map((org: Organization) => org.name);
    },
    get existingFacilityNames() {
      return Array.from(self.facilityMap.values()).map((fac: FullFacility) => fac.name);
    },
  }))
  .actions(self => ({
    getUser() {
      const oidcUser = getOidcUser();
      const sub = oidcUser?.profile?.sub;
      return sub ? { id: sub } : null;
    },
    setDigitalOrderRequestStatus(value: RequestStatus) {
      self.submitDigitalOrderRequestStatus = value;
    },
    setOrganizationsRequestStatus(value: RequestStatus) {
      self.organizationsRequestStatus = value;
    },
    setFacilityRequestStatus(value: RequestStatus) {
      self.facilityRequestStatus = value;
    },
    setCountryRequestStatus(value: RequestStatus) {
      self.countryRequestStatus = value;
    },
    setStateRequestStatus(value: RequestStatus) {
      self.stateRequestStatus = value;
    },
    writeToCurrentOrder(id: string, update: Partial<DigitalOrdersBase>, orderStatus?: string) {
      const order = self.digitalOrders.get(id);
      self.digitalOrders.set(id, {
        ...order,
        ...update,
        orderStatus: orderStatus ?? OrderStatus.INCOMPLETE,
      });
    },
    setOrderStatus(id: string, status: OrderStatus) {
      const order = getSnapshot(self.digitalOrders.get(id));
      const orderClone = cloneDeep(order);
      orderClone.submitted = true;
      orderClone.orderStatus = status;
      self.digitalOrders.set(id, orderClone);
    },
  }))
  .actions(self => ({
    setOrderFulfiller(id) {
      const order = getSnapshot(self.digitalOrders.get(id));
      const orderClone = cloneDeep(order);
      const user = self.getUser();
      orderClone.fulfillmentUser = user.id;
      orderClone.fulfillmentTime = Math.floor(new Date().getTime() / 1000);
      self.digitalOrders.set(id, orderClone);
    },
    scaffold() {
      const uuid = uuidv4();
      const user = self.getUser();
      self.digitalOrders.set(
        uuid,
        DigitalOrdersBaseModel.create({
          id: uuid,
          userId: user.id,
          created: Math.floor(new Date().getTime() / 1000),
          updated: Math.floor(new Date().getTime() / 1000),
          facility: {
            facilities: [],
            organizationId: undefined,
            isActive: '1',
          },
        }),
      );
      return self.digitalOrders.get(uuid);
    },
    submitDigitalOrder: flow(function*(orderId) {
      try {
        self.setDigitalOrderRequestStatus(RequestStatus.PENDING);
        let payload = self.digitalOrders.get(orderId) as DigitalOrdersBase;
        const response: DigitalOrdersSaveResponse = yield self.environment.digitalOrdersApi.saveOrder(
          orderId,
          { ...payload, updated: Math.floor(new Date().getTime() / 1000) },
        );
        if (response.kind === 'ok') {
          self.setDigitalOrderRequestStatus(RequestStatus.DONE);
          return;
        } else {
          throw new Error(response.kind);
        }
      } catch (error) {
        throw new Error(error.message || error);
      }
    }),
    // Finalizes the order (pricing, submitted flag) and submits it, but only commits
    // the finalized order to the store once both API calls have succeeded, so a failed
    // submission never looks submitted (DO-291).
    finalizeAndSubmitOrder: flow(function*(orderId: string, pricingMap: ProductPricingMap) {
      try {
        self.setDigitalOrderRequestStatus(RequestStatus.PENDING);
        const order = getSnapshot(self.digitalOrders.get(orderId));
        const orderClone = cloneDeep(order);
        Object.keys(pricingMap).forEach(productName => {
          orderClone.productDetails[productName].pricing = pricingMap[productName].pricing;
        });
        orderClone.submitted = true;
        orderClone.orderStatus = OrderStatus.SUBMITTED;
        orderClone.updated = Math.floor(new Date().getTime() / 1000);
        const finalizedOrder = (orderClone as unknown) as DigitalOrdersBase;
        const saveResponse: DigitalOrdersSaveResponse = yield self.environment.digitalOrdersApi.saveOrder(
          orderId,
          finalizedOrder,
        );
        if (saveResponse.kind !== 'ok') {
          throw new Error(saveResponse.kind);
        }
        const sendResponse: DigitalOrdersSendResponse = yield self.environment.digitalOrdersApi.sendEmail(
          orderId,
          finalizedOrder,
        );
        if (sendResponse.kind !== 'ok') {
          throw new Error(sendResponse.kind);
        }
        self.digitalOrders.set(orderId, orderClone);
        self.setDigitalOrderRequestStatus(RequestStatus.DONE);
      } catch (error) {
        self.setDigitalOrderRequestStatus(RequestStatus.ERROR);
        throw new Error(error.message || error);
      }
    }),
    loadOrganizations: flow(function*() {
      try {
        self.setOrganizationsRequestStatus(RequestStatus.PENDING);
        const response: GetAllOrganizationsResult = yield self.environment.digitalOrdersApi.loadOrganizations();
        if (response.kind === 'ok') {
          response.data.forEach(org => {
            self.organizationMap.set(org.id.toString(), org);
          });
          self.setOrganizationsRequestStatus(RequestStatus.DONE);
        } else {
          throw new Error(response.kind);
        }
      } catch (error) {
        throw new Error(error.message || error);
      }
    }),
    getOrganizationFacilities: flow(function*(id: string) {
      try {
        self.setFacilityRequestStatus(RequestStatus.PENDING);
        const response: GetSomeFacilitiesResult = yield self.environment.digitalOrdersApi.getOrganizationFacilities(
          id,
        );
        if (response.kind === 'ok') {
          response.data.forEach(facility => {
            self.facilityMap.set(facility.id.toString(), facility);
          });
          self.setFacilityRequestStatus(RequestStatus.DONE);
          return response.data;
        } else {
          throw new Error(response.kind);
        }
      } catch (error) {
        throw new Error(error.message || error);
      }
    }),
    loadAllFacilities: flow(function*() {
      try {
        self.setFacilityRequestStatus(RequestStatus.PENDING);
        const response: GetSomeFacilitiesResult = yield self.environment.digitalOrdersApi.getFacilities();
        if (response.kind === 'ok') {
          response.data.forEach(facility => {
            self.facilityMap.set(facility.id.toString(), facility);
          });
          self.setFacilityRequestStatus(RequestStatus.DONE);
          return response.data;
        } else {
          throw new Error(response.kind);
        }
      } catch (error) {
        throw new Error(error.message || error);
      }
    }),
    loadCountries: flow(function*() {
      try {
        self.setCountryRequestStatus(RequestStatus.PENDING);
        const response: GetAllCountriesResult = yield self.environment.digitalOrdersApi.loadCountries();
        if (response.kind === 'ok') {
          self.countries.replace(response.data);
          self.setCountryRequestStatus(RequestStatus.DONE);
        } else {
          throw new Error(response.kind);
        }
      } catch (error) {
        throw new Error(error.message || error);
      }
    }),
    loadStates: flow(function*() {
      try {
        self.setStateRequestStatus(RequestStatus.PENDING);
        const response: GetAllStatesResult = yield self.environment.digitalOrdersApi.loadStates();
        if (response.kind === 'ok') {
          self.states.replace(response.data);
          self.setStateRequestStatus(RequestStatus.DONE);
        } else {
          throw new Error(response.kind);
        }
      } catch (error) {
        throw new Error(error.message || error);
      }
    }),
    loadDigitalOrders: flow(function*(params: {
      userId?: string;
      status?: string[];
      startDate?: number;
      endDate?: number;
    }) {
      try {
        self.setDigitalOrderRequestStatus(RequestStatus.PENDING);
        const response: GetAllDigitalOrdersResult = yield self.environment.digitalOrdersApi.loadDigitalOrders(
          params,
        );
        if (response.kind === 'ok') {
          response.data.forEach(order => {
            self.digitalOrders.set(order.id.toString(), order);
          });
          self.setDigitalOrderRequestStatus(RequestStatus.DONE);
          return response.data;
        } else {
          throw new Error(response.kind);
        }
      } catch (error) {
        throw new Error(error.message || error);
      }
    }),
    removeDigitalOrderById: flow(function*(orderId) {
      try {
        const response: RemoveDigitalOrderByIdResult = yield self.environment.digitalOrdersApi.removeDigitalOrderById(
          orderId,
        );
        if (response.kind === 'ok') {
          self.digitalOrders.delete(orderId.toString());
          return response.data;
        } else {
          throw new Error(response.kind);
        }
      } catch (error) {
        throw new Error(error.message || error);
      }
    }),
    orgNameAlreadyExists: flow(function*(name: string) {
      const existingOrganization = Array.from(self.organizationMap.values()).find(
        (org: Organization) => org.name.toLowerCase() === name.toLowerCase(),
      );
      return !!existingOrganization;
    }),
  }));
type DigitalOrdersStoreType = typeof DigitalOrdersStoreModel.Type;
export interface DigitalOrdersStore extends DigitalOrdersStoreType {}
type DigitalOrdersStoreSnapshotType = typeof DigitalOrdersStoreModel.SnapshotType;
export interface DigitalOrdersStoreSnapshot extends DigitalOrdersStoreSnapshotType {}
